// Путь: D:\QR\QR-WEB\dashboard\src\components\Profile.jsx
// Название: User Profile
// Версия: 3.2.0 - Simplified 2 columns
// Дата: 06.01.2026

import React, { useState, useEffect } from 'react';
import './Profile.css';

const API_URL = 'https://qr-money.onrender.com/api';

const getFormattedDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('ru-RU');
};

const Profile = ({ userId }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [editForm, setEditForm] = useState({ 
    first_name: '', 
    last_name: '', 
    username: '', 
    phone: ''
  });

  const actualUserId = userId || localStorage.getItem('userId') || 1;
  const authToken = localStorage.getItem('authToken');

  useEffect(() => {
    fetchProfileData();
  }, [actualUserId]);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_URL}/profile/${actualUserId}`, {
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setUser(data.data);
      setEditForm({ 
        first_name: data.data.first_name || '', 
        last_name: data.data.last_name || '', 
        username: data.data.username || '',
        phone: data.data.phone || ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      const res = await fetch(`${API_URL}/profile/${actualUserId}/update`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify(editForm),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error);
      setUser(data.data || data.user);
      setEditing(false);
      setSuccessMessage('✓ Сохранено!');
      setTimeout(() => setSuccessMessage(''), 3000);
      fetchProfileData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const getProfileStatus = () => {
    if (!user) return { level: 0, text: '—', class: '' };
    
    const hasEmail = !!user.email;
    const hasUsername = !!user.username;
    const hasFirstName = !!user.first_name;
    const hasLastName = !!user.last_name;
    const hasPhone = !!user.phone;
    const emailVerified = user.email_verified || user.emailVerified;

    if (hasEmail && hasUsername && hasFirstName && hasLastName && hasPhone && emailVerified) {
      return { level: 3, text: '🟢 Полный', class: 'status-full' };
    }
    if (hasEmail && hasUsername && hasFirstName && hasLastName) {
      return { level: 2, text: '🟡 Стандартный', class: 'status-partial' };
    }
    return { level: 1, text: '🔴 Минимальный', class: 'status-empty' };
  };

  if (loading) {
    return (
      <div className="profile-loading">
        <div className="profile-spinner"></div>
        <p>Загрузка...</p>
      </div>
    );
  }
  
  if (!user) return <div className="profile-error">Профиль не найден</div>;

  const profileStatus = getProfileStatus();

  return (
    <div className="profile-container">
      <h1 className="profile-title">👤 Мой профиль</h1>
      
      {successMessage && <div className="success-msg">{successMessage}</div>}
      {error && <div className="error-msg">{error}</div>}

      <div className="profile-grid-2">
        {/* АККАУНТ */}
        <div className="profile-card">
          <h3>ℹ️ АККАУНТ</h3>
          <div className="info-row"><span>ID:</span><b>{user.id}</b></div>
          <div className="info-row"><span>Ник:</span><b>{user.username || '—'}</b></div>
          <div className="info-row">
            <span>Email:</span>
            <b>
              {user.email}
              {(user.email_verified || user.emailVerified) && <span className="verified-badge"> ✓</span>}
            </b>
          </div>
          <div className="info-row">
            <span>Статус:</span>
            <b className={profileStatus.class}>{profileStatus.text}</b>
          </div>
          <div className="info-row">
            <span>Регистрация:</span>
            <b>{getFormattedDate(user.createdAt || user.created_at)}</b>
          </div>
        </div>

        {/* ЛИЧНЫЕ ДАННЫЕ */}
        <div className="profile-card">
          <h3>✏️ ЛИЧНЫЕ ДАННЫЕ</h3>
          {!editing ? (
            <>
              <div className="info-row">
                <span>Имя:</span>
                <b>{user.first_name || <span className="empty-field">Не указано</span>}</b>
              </div>
              <div className="info-row">
                <span>Фамилия:</span>
                <b>{user.last_name || <span className="empty-field">Не указано</span>}</b>
              </div>
              <div className="info-row">
                <span>Телефон:</span>
                <b>{user.phone || <span className="empty-field">Не указан</span>}</b>
              </div>
              <button className="btn-edit" onClick={() => setEditing(true)}>
                Редактировать
              </button>
            </>
          ) : (
            <div className="edit-form">
              <div className="form-group">
                <label>Имя</label>
                <input 
                  type="text" 
                  placeholder="Введите имя" 
                  value={editForm.first_name} 
                  onChange={(e) => setEditForm({...editForm, first_name: e.target.value})} 
                />
              </div>
              <div className="form-group">
                <label>Фамилия</label>
                <input 
                  type="text" 
                  placeholder="Введите фамилию" 
                  value={editForm.last_name} 
                  onChange={(e) => setEditForm({...editForm, last_name: e.target.value})} 
                />
              </div>
              <div className="form-group">
                <label>Ник</label>
                <input 
                  type="text" 
                  placeholder="Введите ник" 
                  value={editForm.username} 
                  onChange={(e) => setEditForm({...editForm, username: e.target.value})} 
                />
              </div>
              <div className="form-group">
                <label>Телефон</label>
                <input 
                  type="tel" 
                  placeholder="+380..." 
                  value={editForm.phone} 
                  onChange={(e) => setEditForm({...editForm, phone: e.target.value})} 
                />
              </div>
              <div className="edit-buttons">
                <button className="btn-save" onClick={handleSaveProfile} disabled={saving}>
                  {saving ? '...' : '💾 Сохранить'}
                </button>
                <button className="btn-cancel" onClick={() => setEditing(false)}>
                  Отмена
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Profile;